import React, { useMemo, createContext } from 'react'
import { PaginationReq, AgentMemoQuery, PermissionType } from '@golden/gdk-admin'
import { omitBy, isUndefined } from '@golden/utils'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Paper from '@material-ui/core/Paper'
import MuiButton from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import MuiTextField, { TextFieldProps } from '@material-ui/core/TextField'
import OnOffCheckbox, { PropTypes as OnOffCheckboxProps } from '../../default/form/OnOffCheckbox'
import DropDown, { PropTypes as DropDownProps } from '../../default/form/DropDown'
import LoadingAndErrorFrame from '../../default/frames/LoadingAndErrorFrame'
import FormStateProvider from '../../default/form/FormStateProvider'
import FormField from '../../default/form/FormField'
import FormSubmitButton from '../../default/form/FormSubmitButton'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import useT from '../../../i18ns/admin/useT'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { SearchToRequestFunc, useRequestFromSearch, InitialFormFunc, useChangeUrlSubmit } from '../../../utils/default/ComplexFlowHook'
import { guaranteeNotUndefined, guaranteeBetween, pipe, parseInt, getValueFromChangeEvent, getValueFromCheckboxEvent, acceptUndefined, parseBoolean, convertEmptyToUndefined } from '../../../utils/default/FormHelper'
import allRoutes from '../route/route'
import { FormValidation, createDefaultFormState, ValueGetter } from '../../../utils/default/FormHook'
import { useChecker } from '../../../utils/admin/AdminRouteHook'
import PlayerAgentMemoAddButton from './PlayerAgentMemoAddButton'

export type PlayerAgentMemoRequest = PaginationReq & AgentMemoQuery

export const normalizeDepartment = (department: unknown, canReadRc: boolean, canReadCs: boolean): AgentMemoQuery['department'] => {
  if (canReadRc && !canReadCs) return 'rc'
  if (canReadCs && !canReadRc) return 'cs'
  return department === 'cs' ? 'cs' : 'rc'
}

export const createSearchToRequest = (canReadRc: boolean, canReadCs: boolean): SearchToRequestFunc<PlayerAgentMemoRequest> => (search) => {
  const converted = {
    ...search,
    account: search.account,
    department: normalizeDepartment(search.department, canReadRc, canReadCs),
    show_same_chain: acceptUndefined(search.show_same_chain, parseBoolean),
    page: pipe(
      guaranteeNotUndefined,
      parseInt,
      (value) => guaranteeBetween(value, 1, Number.MAX_SAFE_INTEGER)
    )(search.page)
  } as PlayerAgentMemoRequest
  return omitBy(converted, isUndefined) as PlayerAgentMemoRequest
}

export const initialForm: InitialFormFunc<AgentMemoQuery> = (defaultForm) => ({
  account: '',
  department: 'rc',
  show_same_chain: false,
  ...defaultForm
})

const getValueFromEvent: ValueGetter<AgentMemoQuery> = {
  account: getValueFromChangeEvent,
  department: getValueFromChangeEvent,
  show_same_chain: getValueFromCheckboxEvent
}

const createFormToRequest = (canReadRc: boolean, canReadCs: boolean) => (form: AgentMemoQuery): PlayerAgentMemoRequest => {
  const converted = {
    account: convertEmptyToUndefined(form.account),
    department: normalizeDepartment(form.department, canReadRc, canReadCs),
    show_same_chain: form.show_same_chain ? form.show_same_chain : undefined,
    page: 1
  } as PlayerAgentMemoRequest
  return omitBy(converted, isUndefined) as PlayerAgentMemoRequest
}

const FormContext = createContext(createDefaultFormState(initialForm()))

const TextField = React.memo(MuiTextField)

const Button = React.memo(MuiButton)

const departmentOptions: DropDownProps['options'] = [
  { name: 'RC', value: 'rc' },
  { name: 'CS', value: 'cs' }
]

const PlayerOperationLogsForm: React.FC<{ reload: () => void }> = ({ reload }) => {
  const classes = useCommonStyles()
  const { t } = useT()
  const writable = useChecker([
    PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_EDIT,
    PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_EDIT
  ])
  const canReadRc = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_RC_READ])
  const canReadCs = useChecker([PermissionType.PLAYER_MANAGEMENT_AGENTS_MEMO_CS_READ])
  const showDepartment = canReadRc && canReadCs
  const pageFlow = usePageFlow()

  const searchToRequest = useMemo(() => createSearchToRequest(canReadRc, canReadCs), [canReadRc, canReadCs])
  const formToRequest = useMemo(() => createFormToRequest(canReadRc, canReadCs), [canReadRc, canReadCs])
  const request = useRequestFromSearch({ searchToRequest })
  const defaultForm = useMemo(() => {
    if (request) {
      return initialForm({
        ...request
      })
    }
    return initialForm()
  }, [request])

  const handleSubmit = useChangeUrlSubmit({
    toAddNowTimestamp: true,
    formToRequest,
    encodePath: allRoutes.playerAgentsMemo.encodePath
  })

  const validation = useMemo(() => ({
    account: [],
    department: [],
    show_same_chain: []
  } as FormValidation<AgentMemoQuery>), [])

  return (
    <FormStateProvider
      context={FormContext}
      defaultValue={defaultForm}
      onSubmit={handleSubmit}
      validation={validation}
      getValueFromEvent={getValueFromEvent}
    >
      {writable && (
        <Box paddingBottom={2}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <PlayerAgentMemoAddButton reload={reload} />
            </Grid>
          </Grid>
        </Box>
      )}
      <Paper>
        <Box padding={4}>
          <LoadingAndErrorFrame { ...pageFlow.status }>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Box
                  paddingY={1.25}
                  paddingX={2}
                  className={classes.pinkTitleBar}
                >
                  <Typography variant="h5">
                    {t('page.agentsMemo')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormField<AgentMemoQuery, TextFieldProps>
                      context={FormContext}
                      component={TextField}
                      name="account"
                      label={t('common.agentsLayerAccount')}
                      placeholder={t('placeholder.inputAgentsLayerAccount')}
                      fullWidth
                    />
                  </Grid>
                  {showDepartment && (
                    <Grid item xs={12} md={3}>
                      <FormField<AgentMemoQuery, DropDownProps>
                        context={FormContext}
                        component={DropDown}
                        name="department"
                        label={t('common.department')}
                        options={departmentOptions}
                        fullWidth
                        required
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <Grid item xs={12} md={3}>
                  <FormField<AgentMemoQuery, OnOffCheckboxProps>
                    component={OnOffCheckbox}
                    context={FormContext}
                    name="show_same_chain"
                    label={t('common.showSameChain')}
                  />
                </Grid>
              </Grid>
              <Grid item>
                <Grid container justifyContent="flex-end">
                  <Grid item>
                    <FormSubmitButton
                      component={Button}
                      context={FormContext}
                      type="submit"
                      className={classes.purpleGradualButton}
                    >
                      {t('common.search')}
                    </FormSubmitButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </LoadingAndErrorFrame>
        </Box>
      </Paper>
    </FormStateProvider>
  )
}

export default React.memo(PlayerOperationLogsForm)
